import React, { useState, useEffect, useRef } from "react";
import { X, ChevronUp, ChevronDown, Info } from "lucide-react";
import Tooltip from "../addentities/Tooltip";
import apiHelper from "../../apiHelper";
import AlertNotification from "../../components/AlertNotification";

const StockOutModal = ({
  isOpen,
  onClose,
  onConfirm,
  stockInstance,
  sellableQty = 0,
}) => {
  const [stockOutChannel, setStockOutChannel] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({
    quantity: false,
    reason: false,
  });

  const [stockChannels, setStockChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastMessage, setToastMessage] = useState("");
  const [toastAction, setToastAction] = useState("update");
  const [recordName, setRecordName] = useState("stockOut");

  const [openChannel, setOpenChannel] = useState(false);
  const dropdownRef = useRef(null);
  // useEffect(() => {
  //   const fetchStockChannels = async () => {
  //     try {
  //       setLoadingChannels(true);
  //       const response = await apiHelper(
  //         'inventory/stock-channels/',
  //         'GET'
  //       );

  //       if (response.ok) {
  //         const result = await response.json();
  //         if (result.success) {
  //           setStockChannels(result.data);
  //         } else {
  //           console.error('Failed to fetch stock channels:', result.error);
  //           setStockChannels([
  //             { value: 1, label: "Disposal" },
  //             { value: 2, label: "Damage" },
  //             { value: 3, label: "Loss" },
  //             { value: 4, label: "Others" }
  //           ]);
  //         }
  //       } else {
  //         console.error('Failed to fetch stock channels');
  //         setStockChannels([
  //           { value: 1, label: "Disposal" },
  //           { value: 2, label: "Damage" },
  //           { value: 3, label: "Loss" },
  //           { value: 4, label: "Others" }
  //         ]);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching stock channels:', error);
  //       setStockChannels([
  //         { value: 1, label: "Disposal" },
  //         { value: 2, label: "Damage" },
  //         { value: 3, label: "Loss" },
  //         { value: 4, label: "Others" }
  //       ]);
  //     } finally {
  //       setLoadingChannels(false);
  //     }
  //   };

  //   if (isOpen) {
  //     fetchStockChannels();
  //   }
  // }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setStockChannels([
        { value: 1, label: "Disposal" },
        { value: 2, label: "Damage" },
        { value: 3, label: "Loss" },
        { value: 4, label: "Others" },
      ]);
      setLoadingChannels(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setStockOutChannel("");
      setQuantity(1);
      setReason("");
      setErrors({});
      setTouched({ quantity: false, reason: false });
      setShowToast(false);
    }
  }, [isOpen, stockInstance]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenChannel(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const showToastMessage = (type, message, action = "update") => {
    setToastType(type);
    setToastMessage(message);
    setToastAction(action);
    setRecordName("stockOut");
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 5000);
  };

  const handleIncrease = () => {
    const currentQty = Number(quantity) || 0;

    if (currentQty < sellableQty) {
      const newQuantity = currentQty + 1;
      setQuantity(newQuantity);
      setErrors((prev) => ({ ...prev, quantity: null }));
    }
  };

  const handleDecrease = () => {
    const currentQty = Number(quantity) || 1;

    if (currentQty > 1) {
      const newQuantity = currentQty - 1;
      setQuantity(newQuantity);
      setErrors((prev) => ({ ...prev, quantity: null }));
    }
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setQuantity("");
      setErrors((prev) => ({ ...prev, quantity: null }));
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    const numValue = parseInt(value, 10);

    if (isNaN(numValue)) {
      setQuantity("");
    } else if (numValue === 0) {
      setQuantity("");
      setErrors((prev) => ({
        ...prev,
        quantity: "Quantity must be at least 1",
      }));
    } else {
      setQuantity(numValue);
      if (numValue <= sellableQty) {
        setErrors((prev) => ({ ...prev, quantity: null }));
      }
    }
  };

  const handleQuantityBlur = () => {
    setTouched((prev) => ({ ...prev, quantity: true }));

    if (quantity === "" || isNaN(Number(quantity)) || Number(quantity) === 0) {
      setQuantity(1);
      setErrors((prev) => ({
        ...prev,
        quantity: "Quantity must be at least 1",
      }));
      return;
    }

    const numValue = Number(quantity);

    if (numValue > sellableQty) {
      setQuantity(sellableQty);
      setErrors((prev) => ({
        ...prev,
        quantity: `Quantity cannot exceed available sellable items (${sellableQty})`,
      }));
    } else if (numValue < 1) {
      setQuantity(1);
      setErrors((prev) => ({
        ...prev,
        quantity: "Quantity must be at least 1",
      }));
    } else {
      setErrors((prev) => ({ ...prev, quantity: null }));
    }
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
    if (e.target.value.trim()) {
      setErrors((prev) => ({ ...prev, reason: null }));
    }
  };

  const handleReasonBlur = () => {
    setTouched((prev) => ({ ...prev, reason: true }));
    if (!reason.trim()) {
      setErrors((prev) => ({ ...prev, reason: "Reason is required" }));
    }
  };

  // const validateForm = () => {
  //   const newErrors = {};

  //   if (!stockOutChannel) {
  //     newErrors.channel = "Stock-out Channel is required";
  //   }

  //   const numQuantity = Number(quantity);
  //   if (!quantity || isNaN(numQuantity) || numQuantity < 1) {
  //     newErrors.quantity = "Quantity must be at least 1";
  //   } else if (numQuantity > sellableQty) {
  //     newErrors.quantity = `Quantity cannot exceed available sellable items (${sellableQty})`;
  //   }

  //   if (!reason.trim()) {
  //     newErrors.reason = "Reason is required";
  //   }

  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };

  const validateForm = () => {
    const newErrors = {};

    // Stock-out Channel
    if (!stockOutChannel) {
      newErrors.channel = "Stock-out Channel is required";
    }

    // Quantity
    const numQuantity = Number(quantity);
    if (!quantity || isNaN(numQuantity) || numQuantity < 1) {
      newErrors.quantity = "Quantity must be at least 1";
    } else if (numQuantity > sellableQty) {
      newErrors.quantity = `Quantity cannot exceed available sellable items (${sellableQty})`;
    }

    // Reason
    if (!reason.trim()) {
      newErrors.reason = "Reason is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        stock_instance_id: stockInstance?.stock_instance_id,
        stock_balance_id: stockInstance?.stock_balance_id,
        stock_channel_id: parseInt(stockOutChannel),
        quantity: Number(quantity),
        reason: reason,
      };

      const response = await apiHelper("inventory/stock-out/", "POST", payload);

      if (response.ok) {
        const result = await response.json();

        if (result.success) {
          showToastMessage(
            "success",
            "Items Stocked Out Successfully",
            "stockOut"
          );

          // Pass the data to parent component
          onConfirm({
            ...result.data,
            success: true,
          });

          setTimeout(() => {
            onClose();
          }, 500);
        } else {
          showToastMessage(
            "error",
            result.error || "Failed to process stock out",
            "update",
          );
        }
      } else {
        const errorResult = await response.json();
        showToastMessage(
          "error",
          errorResult.error || "Failed to process stock out",
          "update",
        );
      }
    } catch (error) {
      console.error("Stock out error:", error);
      showToastMessage(
        "error",
        "An error occurred while processing stock out",
        "update",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>

      <div className="drawer-overlay" onClick={onClose} />
      <div
        className="entity-drawer modal-popup-cls"
        style={{
          maxWidth: 650,
          margin: "0 auto",
          background: "white",
          borderRadius: 12,
          zIndex: 1050,
          padding: 24,
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
          height: "auto",
          right: "auto",
          overflow: "inherit",
        }}
      >
        <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <h3
          style={{
            textAlign: "left",
            color: "#344050",
            fontSize: "20px",
            fontWeight: "600",
            lineHeight: "normal",
            fontFamily: "Montserrat",
            marginBottom: "12px",
            fontStyle: "normal",
          }}
        >
          Initiate Stock Out
        </h3>

        <p
          style={{
            padding: "0px",
            marginBottom: "24px",
            textAlign: "left",
            color: "#5E6E82",
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "26px",
            fontFamily: "Montserrat",
            fontStyle: "normal",
          }}
        >
          Initiating a stock-out means marking the item as no longer part of
          active inventory. This action only applies to cases such as loss,
          scrap, or other non-sale or gift removals.
        </p>

        <div className="drawer-content">
          <div className="row gappingcls-added">
            <div className="col-12">
              <div className="info-inner tab-panel">
                <div
                  className="field-block"
                  style={{ marginBottom: "16px", padding: "0px" }}
                >
                  <div
                    className="field-label"
                    style={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      marginBottom: "8px",
                      fontSize: "16px",
                      fontFamily: "Montserrat",
                      fontWeight: "600",
                      color: "#212529",
                    }}
                  >
                    Stock-out Channel{" "}
                    <span className="error-color" style={{ color: "red" }}>
                      *
                    </span>
                  </div>
                  {/* <select 
                    className={`form-select new-form-select-cls ${errors.channel ? 'is-invalid' : ''}`}
                    value={stockOutChannel}
                    onChange={(e) => {
                      setStockOutChannel(e.target.value);
                      if (e.target.value) {
                        setErrors(prev => ({ ...prev, channel: null }));
                      }
                    }}
                    disabled={loadingChannels}
                    style={{
                      borderColor: errors.channel ? "#DC3545" : "#6c757d",
                    }}
                  >
                    <option value="">Select... </option>
                    {stockChannels.map((channel) => (
                      <option key={channel.value} value={channel.value}>
                        {channel.label}
                      </option>
                    ))}
                  </select> */}
                  <div
                    ref={dropdownRef}
                    className={`custom-select-display ${errors.channel ? "invalid-select is-invalid" : ""}`}
                    onClick={() => setOpenChannel(!openChannel)}
                  >
                    <span>
                      {stockOutChannel
                        ? stockChannels.find((c) => c.value == stockOutChannel)
                            ?.label
                        : "Select..."}
                    </span>

                    <span
                      className="custom-select-arrow"
                      style={{ float: "right" }}
                    >
                      {openChannel ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </span>

                    {openChannel && (
                      <div className="custom-select-dropdown">
                        {stockChannels.map((channel) => (
                          <div
                            key={channel.value}
                            className={`custom-select-option ${
                              stockOutChannel == channel.value
                                ? "selected-option"
                                : ""
                            }`}
                            onClick={() => {
                              setStockOutChannel(channel.value);
                              setErrors((prev) => ({ ...prev, channel: null }));
                              setOpenChannel(false);
                            }}
                          >
                            {channel.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {loadingChannels && (
                    <div
                      className="text-muted"
                      style={{ fontSize: "12px", marginTop: "4px" }}
                    >
                      Loading channels...
                    </div>
                  )}
                  {errors.channel && (
                    <div
                      className="invalid-feedback"
                      style={{
                        color: "#DC3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.channel}
                    </div>
                  )}
                </div>

                <div
                  className="field-block"
                  style={{ marginBottom: "16px", padding: "0px" }}
                >
                  <div
                    className="field-label"
                    style={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      marginBottom: "8px",
                      fontSize: "16px",
                      fontFamily: "Montserrat",
                      fontWeight: "600",
                      color: "#212529",
                    }}
                  >
                    Quantity{" "}
                    <span className="error-color" style={{ color: "red" }}>
                      *
                    </span>
                    {/* <Tooltip  title = "Quantity" body="Only Sellable items can be stocked out. Please input a number smaller than Sellable Qty">
                      <Info size={16} className="info-icon" />
                    </Tooltip> */}
                    <Tooltip
                      title="Quantity"
                      body='Only "Sellable" items can be stocked out. Please input a number smaller than "Sellable" Qty.'
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="15"
                        viewBox="0 0 10 10"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_3856_30308)">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375   
                                              6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594   
                                              6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594   
                                              2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688   
                                              8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322   
                                              7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784   
                                              6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0   
                                              3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z"
                            fill="#5E6E82"
                          />
                          <path
                            d="M5.58122 4.11719L4.14997 4.29656L4.09872 4.53406L4.37997 4.58594C4.56372 4.62969   
                                            4.59997 4.69594 4.55997 4.87906L4.09872 7.04656C3.97747 7.60719 4.16435 7.87094   
                                            4.60372 7.87094C4.94435 7.87094 5.33997 7.71344 5.51935 7.49719L5.57434 7.23719C5.44934   
                                            7.34719 5.26684 7.39094 5.14559 7.39094C4.97372 7.39094 4.91122 7.27031 4.95559   
                                            7.05781L5.58122 4.11719Z"
                            fill="#5E6E82"
                          />
                          <path
                            d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875   
                                            5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375   
                                            5 3.4375Z"
                            fill="#5E6E82"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_3856_30308">
                            <rect width="10" height="10" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </Tooltip>
                  </div>

                  <div
                    className="custom-quantity-wrapper"
                    style={{ position: "relative" }}
                  >
                    <input
                      type="text"
                      value={quantity}
                      onChange={handleQuantityChange}
                      onBlur={handleQuantityBlur}
                      className={`custom-quantity-input ${errors.quantity ? "is-invalid" : ""}`}
                      style={{
                        width: "100%",
                        padding: "8px 40px 8px 12px",
                        // border: `1px solid ${errors.quantity ? "#DC3545" : "none"}`,
                        border:'none',
                        borderRadius: "8px",
                        fontSize: "14px",
                      }}
                    />
                    <div
                      className="custom-spinner"
                      style={{
                        position: "absolute",
                        right: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        flexDirection: "column",
                        borderLeft: "none",
                        background: "none",
                      }}
                    >
                      <div
                        onClick={handleIncrease}
                        style={{
                          display: "inline-block",
                          marginBottom: "-12px",
                          cursor:
                            (Number(quantity) || 0) >= sellableQty
                              ? "not-allowed"
                              : "pointer",
                          padding: "2px 4px",
                          userSelect: "none",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          style={{
                            opacity:
                              (Number(quantity) || 0) >= sellableQty ? 0.5 : 1,
                          }}
                        >
                          <path
                            d="M12 10L8 6L4 10"
                            stroke="#212529"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      <div
                        onClick={handleDecrease}
                        style={{
                          display: "inline-block",
                          cursor:
                            (Number(quantity) || 0) <= 1
                              ? "not-allowed"
                              : "pointer",
                          padding: "2px 4px",
                          userSelect: "none",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          style={{
                            opacity: (Number(quantity) || 0) <= 1 ? 0.5 : 1,
                          }}
                        >
                          <path
                            d="M4 6L8 10L12 6"
                            stroke="#212529"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  {errors.quantity && (
                    <div
                      className="invalid-feedback"
                      style={{
                        color: "#DC3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.quantity}
                    </div>
                  )}
                  {/* <div style={{ fontSize: "12px", color: "#6C757D", marginTop: "4px" }}>
                    Available sellable items: {sellableQty}
                  </div> */}
                </div>

                <div className="field-block" style={{ padding: "0px" }}>
                  <div
                    className="field-label"
                    style={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      marginBottom: "8px",
                      fontSize: "16px",
                      fontFamily: "Montserrat",
                      fontWeight: "600",
                      color: "#212529",
                    }}
                  >
                    Reason{" "}
                    <span className="error-color" style={{ color: "red" }}>
                      *
                    </span>
                  </div>
                  <textarea
                    className={`form-control w-100 common-textarea-all ${errors.reason ? "is-invalid" : ""}`}
                    rows="3"
                    placeholder="Please input your reason..."
                    value={reason}
                    onChange={handleReasonChange}
                    onBlur={handleReasonBlur}
                    style={{
                      borderColor: errors.reason ? "#DC3545" : "#6c757d",
                      borderRadius: "8px",
                      resize: "none",
                      height: "100px",
                      overflowY: "auto",
                      fontFamily: '"Montserrat", sans-serif',
                      color: "#212529",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "18px",
                      boxShadow: "none",
                    }}
                  />
                  {errors.reason && (
                    <div
                      className="invalid-feedback"
                      style={{
                        color: "#DC3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.reason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="drawer-footer"
          style={{
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button
            className="btn-cancel"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: "8px 16px",
              border: "1px solid #212529",
              borderRadius: "8px",
              background: "white",
              color: "#212529",
              fontWeight: 600,
              cursor: "pointer",
              opacity: submitting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSubmit}
            disabled={loadingChannels || submitting}
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "8px",
              background: loadingChannels || submitting ? "#6c757d" : "#212529",
              color: "white",
              fontWeight: 600,
              cursor: loadingChannels || submitting ? "not-allowed" : "pointer",
              opacity: loadingChannels || submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Processing..." : "Confirm"}
          </button>
        </div>
        <style>
          {`
            .custom-select-display {
                border: 1px solid #6c757d;
                padding: 8px 12px;
                border-radius: 8px;
                cursor: pointer;
                background: white;
                font-size: 14px;
                user-select: none;
              }

              .custom-select-display.invalid-select {
                border-color: #DC3545 !important;
              }

              .custom-select-dropdown {
                margin-top: 10px;
                border: 1px solid #e9ecef;
                border-radius: 8px;
                background: white;
                position: absolute;
                width: 93%;
                z-index: 9999;
                max-height: 200px;
                overflow-y: auto;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                margin-left:-13px;
              }

              .custom-select-option {
                padding: 8px 12px;
                font-size: 14px;
                cursor: pointer;
              }

              .custom-select-option:hover {
                background: #FFFEF0;
                color: #000;
              }

              .selected-option {
                background: #FFFEF0;
                color: #000;
              }

              .entity-drawer .drawer-content .tab-panel .field-block, 
              .filter-drawer .drawer-content .tab-panel .field-block{
                border-bottom:none;
              }
              
              textarea.is-invalid {
                background-image: none !important;
              }

          `}
        </style>
      </div>
    </>
  );
};

export default StockOutModal;
